import React, { useState, useRef, useEffect } from "react";
import "./createLegalDoc.css";
import LeftPanel from "../components/leftPanel";
import { logEvent } from "firebase/analytics";
import { config } from "../config";
import { analytics } from "../components/firebase";
import { useLocation, useNavigate, useParams  } from "react-router-dom";
import FullScreenLoader from "../components/FullScreenLoader";
import { toast, ToastContainer } from "react-toastify";
import GoogleButton from "../components/GoogleAuth";
import { Helmet } from "react-helmet-async";
const CriminalCharge = () => {
  const [legalDocumentType, setLegalDocumentType] = useState("");
  const [keyPoints, setKeyPoints] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [language, setLanguage] = useState("English");
  const [file, setFile] = useState(null);
  const googleAuthRef = useRef(null);
  const [Topic, setTopic] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(true);

  const textAreaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    document.documentElement.style.setProperty("--main-color", config.color);
    const initialText = location.state?.text || "";
    setTopic(initialText);
    setLegalDocumentType(initialText);
  }, [location.state]);

  const { slug } = useParams();


  const readableTitle = slug
    .replace(/-/g, " ")     
    .replace("templates", "") 
    .trim(); 


  const finalTitle = readableTitle || "Create AI Legal Document";
  const canonicalUrl = `https://legaldocumentgenerator.com/${slug}`;


  const [inputTitle, setInputTitle] = useState("Untitled Document");
  const [initialText, setInitialText] = useState("");

  useEffect(() => {
    if (initialText.trim() && inputTitle === "Untitled Document") {
      const firstLine = initialText.split("\n")[0].trim();
      if (firstLine.length > 0) {
        setInputTitle(firstLine.slice(0, 50)); 
      }
    }
  }, [initialText]); 

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 3 * 1024 * 1024) {
        toast.error("File size must be less than 3MB.");
        return;
      }
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      if (["pdf", "docx"].includes(fileExtension)) {
        setFile(selectedFile);
      } else {
        toast.error("Unsupported file type.");
      }
    }
  };

  const handleGenerate = async () => {
    setErrors({});
    let hasError = false;

    logEvent(analytics, "Generate Legal Document", {
      button_name: "Generate_legal_doc",
    });

    if (!legalDocumentType) {
      setErrors((prev) => ({
        ...prev,
        legalDocumentType: "Document Type is required.",
      }));
      hasError = true;
    }
    if (!jurisdiction) {
      setErrors((prev) => ({
        ...prev,
        jurisdiction: "Jurisdiction is required.",
      }));
      hasError = true;
    }
    if (hasError) return;

    setIsLoading(true);
    setIsButtonVisible(false);
    document.body.classList.add("no-scroll");

    try {
      const requestBody = new FormData();
      requestBody.append("keyPoints", keyPoints);
      requestBody.append("legalDocumentType", legalDocumentType);
      requestBody.append("SupportiveInput", jurisdiction);
      requestBody.append("selectedLanguage", language);

      if (file) {
        requestBody.append("pdf_file", file);
      }

     

      const generationResponse = await fetch(
        `${config.baseURL}/default_gen.php`,
        {
          method: "POST",
          credentials: "include",
          body: requestBody,
        }
      );

      const rawResponse = await generationResponse.text();
      let data;
      try {
        data = JSON.parse(rawResponse);
      } catch {
        toast.error("Please re-try after some time");
        return;
      }

      if (data.error) {
        if (data.error === "Authentication failed") {
          googleAuthRef.current?.login();
          return;
        }
        if (data.redirect === "premium") {
          navigate(`/premium?id=${data.premiumId}`);
          return;
        }
        toast.error("Please re-try after some time");
        return;
      }

      if (data.success && data.resultText) {
        navigate("/result", {
          state: { result: data.resultText, history_id: data.history_id },
        });
        setTopic("");
        setLegalDocumentType("");
        setKeyPoints("");
        setJurisdiction("");
        setLanguage("English");
        setFile(null);
      } else {
        toast.error("Please re-try after some time");
      }
    } catch (error) {
      toast.error("Please re-try after some time");
      logEvent(analytics, "error", {
        error_type: "Legal Document Api Failed",
        error_message: "Failed to generate document due to API error.",
        action: "generate_button_click",
      });
    } finally {
      setIsLoading(false);
      document.body.classList.remove("no-scroll");
    }
  };

  const handleLoginSuccess = () => {
    const pending = localStorage.getItem("pendingDefaultGen");
    if (pending) {
      const data = JSON.parse(pending);
      setKeyPoints(data.keyPoints);
      setLegalDocumentType(data.legalDocumentType);
      setJurisdiction(data.jurisdiction);
      setLanguage(data.language);
      localStorage.removeItem("pendingDefaultGen");
      toast.success("Logged in – go ahead and click Generate again!");
    }
  };

  const handleLoginFailure = () => {};

  useEffect(() => {
    const timers = [];
    Object.entries(errors).forEach(([key, value]) => {
      if (value) {
        const timer = setTimeout(() => {
          setErrors((prev) => ({ ...prev, [key]: null }));
        }, 5000);
        timers.push(timer);
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [errors]);

  return (
    <>
     <Helmet key="landing">
     <title>{`${finalTitle} | AI Legal Document Generator`}</title>
        <meta
          name="description"
          content={`Generate ${finalTitle} instantly using our AI-powered legal document generator. Customize your document with ease and download in minutes.`}
        />
        <meta
          name="keywords"
          content={`${finalTitle}, AI legal document generator, ${slug.replace(/-/g, " ")} template, legal documents`}
        />
        <link rel="canonical" href={canonicalUrl} />
          </Helmet>
      <div className="create_background">
        {isLoading && <FullScreenLoader />}
        <LeftPanel />
        <div className="create_main">
          <div className="create_title">
            <img src={config.Backbutton} alt="back" onClick={handleBackClick} />
            Create AI Legal Document
          </div>
          <div className="create_form_grid">
            <div className="create_inputbox1">
              Legal Document Type
              <textarea
                className={`textarea_input ${
                  errors.legalDocumentType ? "error" : ""
                }`}
                name="legalDocumentType"
                placeholder="Write here"
                value={legalDocumentType}
                onChange={(e) => setLegalDocumentType(e.target.value)}
                maxLength={1000}
              />
              {errors.legalDocumentType && (
                <div className="error-message">{errors.legalDocumentType}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Keypoints (optional)
              <div className="textarea_wrapper">
                <textarea
                  className={`textarea_input ${
                    errors.keyPoints ? "error" : ""
                  }`}
                  name="keyPoints"
                  placeholder="Enter key details..."
                  value={keyPoints}
                  onChange={(e) => setKeyPoints(e.target.value)}
                  maxLength={1000}
                />
                {!file ? (
                  <label className="custom_file_upload">
                    <img
                      src={config.uploadFile}
                      alt="Upload"
                      className="upload_icon"
                    />
                    Upload File
                    <input
                      type="file"
                      accept=".pdf, .docx"
                      onChange={handleFileChange}
                      className="hidden_file_input"
                    />
                  </label>
                ) : (
                  <div className="uploaded_file_box">
                    <img
                      src={config.uploadedDoc}
                      alt="File"
                      className="file_icon"
                    />
                    <span className="file_name">{file.name}</span>
                    <button
                      className="remove_file_btn"
                      onClick={() => setFile(null)}
                    >
                      ✖
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="create_inputbox1">
              Your jurisdiction
              <textarea
                className={`textarea_input ${
                  errors.jurisdiction ? "error" : ""
                }`}
                name="jurisdiction"
                placeholder="Enter the country where the document will be valid"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                maxLength={1000}
              />
              {errors.jurisdiction && (
                <div className="error-message">{errors.jurisdiction}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Language
              <select
                className="text_input"
                name="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="English">English</option>
                <option value="Arabic">Arabic (RTL)</option>
                <option value="Bulgarian">Bulgarian</option>
                <option value="Chinese">Chinese (Simplified)</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Hebrew">Hebrew (RTL)</option>
                <option value="Hindi">Hindi</option>
                <option value="Indonesian">Indonesian</option>
                <option value="Italian">Italian</option>
                <option value="Japanese">Japanese</option>
                <option value="Portuguese">Portuguese</option>
                <option value="Punjabi">Punjabi</option>
                <option value="Russian">Russian</option>
                <option value="Spanish">Spanish</option>
                <option value="Swahili">Swahili</option>
                <option value="Tagalog">Tagalog</option>
                <option value="Urdu">Urdu</option>
              </select>
            </div>
          </div>
          <ToastContainer />
          <GoogleButton
            id="loginHidden"
            ref={googleAuthRef}
            onLoginSuccess={handleLoginSuccess}
            onLoginFailure={handleLoginFailure}
          />
          <button className="generate_btn" onClick={handleGenerate}>
            Generate for me
          </button>
        </div>
      </div>
    </>
  );
};

export default CriminalCharge;
